/**
/**
 * ReviewsCourse Component
 *
 * Displays the course reviews section, including user review form, average ratings, star breakdown, and user reviews list.
 * Passes relevant props to child components for dynamic rendering and state management.
 *
 * Features:
 * - Conditional rendering of user review form based on access
 * - Displays average star rating and breakdown
 * - Renders user reviews list with pagination
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {boolean} props.accessCourse - Whether the user has access to the course
 * @param {Function} props.showWriteReviewHandler - Handler to toggle review form
 * @param {boolean} props.showWriteReview - Whether the review form is shown
 * @param {Object} props.dataCourse - Data object containing course info for review
 * @param {Object} props.courseReviews - Data object containing course reviews and ratings
 * @returns {JSX.Element} The rendered course reviews section
 */
import PropTypes from 'prop-types';
import ReactStars from 'react-rating-stars-component';
import LinearProgress from '@mui/material/LinearProgress';
import UserReview from './UserReview/UserReview';
import UsersReviews from './UsersReviews/UsersReviews';
import classes from './ReviewsCourse.module.css';

const ReviewsCourse = props => {
  return (
    <div
      className={
        props.accessCourse
          ? classes.productLearnInfoAccessSection
          : classes.productLearnInfoSection
      }
    >
      {/* Conditional rendering: user review form if user has access */}
      {props.accessCourse && (
        <UserReview
          dataCourse={props.dataCourse} // Prop passed: course data for review
          showWriteReviewHandler={props.showWriteReviewHandler} // Prop passed: handler to toggle review form
          showWriteReview={props.showWriteReview} // Prop passed: review form visibility
        />
      )}
      {/* Conditional rendering: reviews section if review form is not shown */}
      {!props.showWriteReview && (
        <>
          <h2 className={classes.includesTitle}>
            Valoración de alumnos
          </h2>
          <>
            <ReactStars
              edit={false}
              count={5}
              isHalf={true}
              size={24}
              classNames={classes.avgStars} // Prop passed: star rating styling
              activeColor="#e6a817"
              emptyStarColor="#d6d6d6"
              value={
                props.courseReviews
                  ? props.courseReviews.averageStars
                  : 2
              } // Prop passed: average star rating value
            />
            <p className={classes.starRatingValueText}>
              {props.courseReviews.averageStars
                ? props.courseReviews.averageStars % 2 === 1
                  ? Math.trunc(props.courseReviews.averageStars)
                  : parseFloat(
                      props.courseReviews.averageStars
                    ).toFixed(1)
                : 0}{' '}
              de 5
            </p>
            {props.courseReviews.reviewsStars.map(reviewStar => (
              <div
                key={reviewStar.star}
                className={classes.avgBarContainer}
              >
                <p className={classes.starRatingText}>
                  {reviewStar.star} Estrellas
                  {/* Prop passed: star value */}
                </p>
                <LinearProgress
                  classes={{
                    barColorPrimary: classes.linearBarColorPrimary
                  }} // Prop passed: progress bar styling
                  className={classes.progressBar}
                  variant="determinate"
                  value={reviewStar.value} // Prop passed: progress value
                />
                <p className={classes.percentText}>
                  {parseFloat(reviewStar.value).toFixed(0)}%
                  {/* Prop passed: percent value */}
                </p>
              </div>
            ))}
          </>
          <UsersReviews reviewsList={props.courseReviews.list} />
          {/* Prop passed: list of user reviews */}
        </>
      )}
    </div>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
ReviewsCourse.propTypes = {
  /**
   * Boolean indicating whether the user has access to the course
   * Passed as prop to control review access
   */
  accessCourse: PropTypes.bool,
  /**
   * Handler to toggle review form
   * Passed as prop to UserReview
   */
  showWriteReviewHandler: PropTypes.func,
  /**
   * Boolean indicating whether the review form is shown
   * Passed as prop to UserReview
   */
  showWriteReview: PropTypes.bool,
  /**
   * Data object containing course info for review
   * Passed as prop to UserReview and reviews section
   */
  dataCourse: PropTypes.object,
  /**
   * Data object containing course reviews and ratings
   * Passed as prop to reviews section and UsersReviews
   */
  courseReviews: PropTypes.object
};

ReviewsCourse.defaultProps = {
  accessCourse: false,
  showWriteReviewHandler: () => {},
  showWriteReview: false,
  dataCourse: {},
  courseReviews: {
    averageStars: 0,
    reviewsStars: [],
    list: []
  }
};

export default ReviewsCourse;
